/**
 *  @file       dcp-assert.js
 *              Simple assertion module for DCP. Assertions are only
 *              evaluated for debug builds, except for the security-named
 *              assertions.
 *
 *  @author     Wes Garland, wes@kingsds.network
 *  @date       Dec 2019
 */
'use strict';

var isDebugBuild = undefined;
const haveWebPack = typeof __DCP_CLIENT__ !== 'undefined';
exports.assertTriggersDebugger = !!process.env.DCP_ASSERT_TRIGGERS_DEBUGGER;
exports.assertTestTriggersDebugger = !!process.env.DCP_ASSERT_TEST_TRIGGERS_DEBUGGER;

/**
 * Sets whether this assertion module is running in a debug mode  or not. If not called
 * before the first assertion, then we figure this out by checking the module that DCP
 * was configured in with ./configure.sh. The difference is that non-security assertions
 * are ignored during production builds.
 *
 * @note this code test to run very early in the process due to eager module initialization,
 *       especially while loading the webpack bundle, which might not have actually set
 *       the correct dcpConfig.build yet, so it's stuck at "bootstrap", which we treat mostly
 *       like a debug build.
 *
 * @param      idb     {boolean|undefined} falsey if this is release build; truey if this is a debug build; undefined to detect
 */
exports.setDebugBuild = function dcpAssert$$setDebugBuild(idb)
{
  if (typeof idb === 'undefined')
    idb = (require('dcp/common/dcp-build').build === 'debug');

  /* In release mode, rewrite the non-security assertions as fast dummy functions */
  if (!idb)
  {
    let dummy = function dcpAssert$dummy(){ return false; /* allows us test for assert disabled */ };
    for (let assertion of assertionList) {
      if (typeof exports[assertion] === 'function')
        exports[assertion] = dummy;
    }
  }
  else
  {
    for (let assertion of assertionList)
      exports[assertion] = exports.always[assertion];
  }
}

let assertStackDepth = 0;
/**
 * Takes a message to print when asserting, followed by arguments for the assert()
 * function. Edits the stack to hide internal details.
 */
const msgAssert = exports.msgAssert = function dcpAssert$$msgAssert(message)
{
  if (typeof isDebugBuild === 'undefined')
    exports.setDebugBuild();

  if (arguments.length === 0)
    return true; /* allow us to test for enabled asserts */

  if (exports.assertTestTriggersDebugger)
  {
    if (haveWebPack)
      eval('debugger')
    else
     debugger; // allow-debugger
  }

  if (isDebugBuild && !(assertStackDepth >= 0))
    throw new Error('assert stack corrupted, depth is ' + assertStackDepth);

  for (let i=1; i < arguments.length; i++)
  {
    let value = arguments[i];
    if (!value) {
      if (exports.assertTriggersDebugger)
      {
        if (haveWebPack)
          eval('debugger')
        else
          debugger; // allow-debugger
      }

      const error = new Error('Assertion failure');

      if (error.stack)
      {
        /* - location marks where the assert call was.
         * - locationParse 2nd clause is unusual because peter's vm eval in ctx is not filling in
         *   the function name portion of the stack, so it winds up just being a raw path.
         */
        const stack = error.stack.split('\n');
        const location = stack[2 + assertStackDepth];
        const locationParse = location.match(/(\()([^)]*)(\))/) || location.match(/(at\s*)(.*:[0-9]+:[0-9]+)/);
        const doubleCheck = /^\s*at dcpAssert\$\$/.test(stack[1 + assertStackDepth]);

        if (locationParse && doubleCheck)
        {
          /* rip the assertion library out of the stack */
          error.message += message ? ` - ${message}` : (' at ' + locationParse[2]);
          stack[0] = `${error.name}: ${error.message}`;
          stack.splice(1, 1 + assertStackDepth);
          error.stack = stack.join('\n');
        }
      }

      if (haveWebPack)
      {
        const stack = error.stack.split('\n');
        let trimCount = 0;
        for (let entry of stack)
        {
          if (/\/src\/common\/dcp-assert\.js\??:[0-9]+:[0-9]+$/.test(entry)) /* matches spidermonkey stacks */
            trimCount++;
          else
            break;
        }

        if (trimCount)
          stack.splice(0, 2);
        error.stack = stack.join('\n');
      }

      error.code = 'EASSERT';
      throw error;
    }
  }
}

/** Generic assertion mechanism. Throws if any argument is not true. */
let assert = exports.assert = function dcpAssert$$assert() {
  const argv = Array.from(arguments);
  argv.unshift(false);
  try
  {
    assertStackDepth++;
    msgAssert.apply(null, argv);
  }
  finally
  {
    assertStackDepth--;
  }
}

/** Evaluate an expression; assert if the result is not true */
exports.assertEval = function dcpAssert$$assertEval(expr) {
  try
  {
    assertStackDepth++;
    assert(eval(expr));
  }
  finally
  {
    assertStackDepth--;
  }
}

/** Assert to that two values are == equivalent */
exports.assertEq2 = function dcpAssert$$assertEq2(lValue, rValue) {
  try
  {
    assertStackDepth++;
    assert(lValue == rValue)
  }
  finally
  {
    assertStackDepth--;
  }
}

/**
 * Asserts that two values are the same in terms of strict equality (===).
 * Can pass an optional message describing the assertion being made.
 *
 * @param {any} expected The expected value to test for equality
 * @param {any} actual The actual value to compare teh expected value against
 * @param {string} [message=''] An message appended to the assertion error
 */
exports.assertEq3 = function dcpAssert$$assertEq3(
  expected,
  actual,
  message = '',
) {
  try {
    assertStackDepth++;
    assert(expected === actual);
  } catch (e) {
    if (message) {
      e.message += `: ${message}`;
    }

    e.message += ` (${expected} !== ${actual})`;
    throw e;
  }
  finally
  {
    assertStackDepth--;
  }
};

/** Assert to that two values are not == equivalent */
exports.assertNeq2 = function dcpAssert$$assertNeq2(lValue, rValue) {
  try
  {
    assertStackDepth++;
    assert(lValue != rValue);
  }
  finally
  {
    assertStackDepth--;
  }
}

/** Assert to that two values are not the same */
exports.assertNeq3 = function dcpAssert$$assertNeq3(lValue, rValue) {
  try
  {
    assertStackDepth++;
    assert(lValue !== rValue);
  }
  finally
  {
    assertStackDepth--;
  }
}

/**
 *  Assertion that ensures a given statement will throw a given exception.
 *  @param      statement       {function}       function to invoke which is expected to throw
 *  @param      statement       {string}         source code of statement which is evaluated with direct-eval
 *                                               and expected to throw
 *  @param      code [optional] {string}         expected value of the exception's code property
 *  @param      ctor [optional] {function}       function which is expected on the exceptions prototype chain
 *  @returns    true if expectations were met
 */
exports.assertThrows = function dcpAssert$$assertThrows(statement, code, ctor) {
  var threw;

  if (typeof statement === 'string') {
    statement = function shouldThrow_statement() { eval(arguments[0]) };
  }
  if (arguments.length === 2 && typeof code === 'function') {
    ctor = code;
    code = undefined;
  }

  try
  {
    assertStackDepth++;
    try {
      statement();
      threw = false;
    } catch(e) {
      threw = true;
      if (code)
        assert(e.code === code);
      if (ctor)
        assert(e instanceof ctor);
    }

    assert(threw === true);
  }
  finally
  {
    assertStackDepth--;
  }
}

/**
 * Assertion that ensures a given collection contains a given element.
 *
 * @param {any} haystack        The collection to search; must be a Set, Map, Array or Array-like object.
 * @param {any} needle          The element to search for
 */
exports.assertHas = function dcpAssert$$assertHas(haystack, needle) {
  try
  {
    assertStackDepth++;
    if (Array.isArray(haystack))
      assert(haystack.indexOf(needle) !== -1);
    else if (needle instanceof Set || needle instanceof Map)
      assert(haystack.has(needle));
    else
      assert(Array.from(haystack).indexOf(needle) !== -1);
  }
  finally
  {
    assertStackDepth--;
  }
}

/**
 * Assertion that ensures a given value is of a given type or instance of a given class.
 */
exports.assertIsA = function dcpAssert$$assertIsA(value, type) {
  try
  {
    assertStackDepth++;
    if (typeof type === 'string')
      msgAssert('value is not typeof ' + type, typeof value === type);
    else
    {
      msgAssert('value is not an object', value instanceof Object);
      if (type.name)
        msgAssert(`value is not an instance of ${type.name}`, value instanceof type);
      else
        assert(value instanceof type);
    }
  }
  finally
  {
    assertStackDepth--;
  }
}

/* *** All assertions must be defined above here *** */
const assertionList = Object.keys(exports);

/** Add the security assertions (not disabled by debug build) */
for (let assertion of assertionList) {
  let securityAssertion = 'security' + assertion[0].toUpperCase() + assertion.slice(1);
  exports[securityAssertion] = exports[assertion];
}

/** Add the 'always' assertions (also not disabled by debug build) */
exports.always = {};
for (let assertion of assertionList) {
  exports.always[assertion] = exports[assertion];
}

if (typeof isDebugBuild === 'undefined')
  exports.setDebugBuild();
